import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Subscription toggle web component for cart line items.
 * Renders as a toggle switch + "Subscribe & Save" label.
 * When toggled on, reveals a frequency dropdown below.
 *
 * @typedef {object} SubscriptionToggleRefs
 * @property {HTMLButtonElement} toggleSwitch - The toggle switch button (role="switch")
 * @property {HTMLElement} panel - The frequency panel shown when subscribed
 * @property {HTMLSelectElement} frequencySelect - Delivery frequency dropdown
 *
 * @extends {Component<SubscriptionToggleRefs>}
 */
class SubscriptionToggle extends Component {
  requiredRefs = ['toggleSwitch', 'panel', 'frequencySelect'];

  /** @type {number} */
  #line;
  /** @type {number} */
  #quantity;
  /** @type {string|null} What the in-flight cart update is: 'on' | 'off' | 'frequency_change' */
  #pendingAction = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache static values once — dataset doesn't change for this element's lifetime
    this.#line = parseInt(this.dataset.line);
    this.#quantity = parseInt(this.dataset.quantity) || 1;

    this.refs.toggleSwitch.addEventListener('click', this.#onToggle);
    this.refs.frequencySelect.addEventListener('change', this.#onFrequencyChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.toggleSwitch.removeEventListener('click', this.#onToggle);
    this.refs.frequencySelect.removeEventListener('change', this.#onFrequencyChange);
  }

  #onToggle = () => {
    // No optimistic flip (R28): the switch keeps its current visible state
    // until the cart responds and the section re-renders with the real one.
    // Here we only read the current state to decide intent and which plan to
    // send; the pending (disabled) state is shown by #updateCart.
    const isChecked = this.refs.toggleSwitch.getAttribute('aria-checked') === 'true';

    if (isChecked) {
      this.#pendingAction = 'off';
      this.#updateCart('');
    } else {
      const planId = parseInt(this.dataset.defaultPlan || '0');
      if (!planId) return;

      this.#pendingAction = 'on';
      this.#updateCart(planId);
    }
  };

  #onFrequencyChange = () => {
    this.#pendingAction = 'frequency_change';
    this.#updateCart(parseInt(this.refs.frequencySelect.value));
  };

  /**
   * @param {number|string} sellingPlan - Plan ID or '' to remove subscription
   */
  async #updateCart(sellingPlan) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const cartItems = this.closest('cart-items-component');

    const body = JSON.stringify({
      line: this.#line,
      quantity: this.#quantity,
      selling_plan: sellingPlan,
      sections: sectionId,
      sections_url: window.location.pathname,
    });

    // R27: block every cart control and the checkout button while this update
    // is in flight, reusing the same busy state the quantity path uses. R28:
    // the switch shows a pending (disabled) state, never a flipped one.
    this.refs.toggleSwitch.disabled = true;
    cartItems?.setBusy(true);

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors) {
        console.error('[subscription-toggle] Cart error:', data.errors);
        // Nothing was flipped optimistically, so there is nothing to revert.
        return;
      }

      const sectionHtml = data.sections?.[sectionId];
      if (sectionHtml) {
        morphSection(sectionId, sectionHtml);

        document.dispatchEvent(
          new CartUpdateEvent(data, sectionId, {
            itemCount: data.item_count,
            source: 'subscription-toggle',
            sections: data.sections,
          })
        );

        // Cart-side subscription intent for GA4 (via GTM). Failures never
        // reach here, so only successful cart updates are recorded. Clear the
        // pending action once consumed so a later update can't inherit a stale
        // label if it ever runs without setting one.
        const action = this.#pendingAction || (sellingPlan ? 'on' : 'off');
        this.#pendingAction = null;
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'cart_subscription_toggle',
            action,
            selling_plan_id: String(sellingPlan || ''),
            variant_id: this.dataset.variantId || '',
            item_title: this.dataset.itemTitle || '',
          });
        } catch (e) {
          /* never break the cart over analytics */
        }
      }
    } catch (error) {
      console.error('[subscription-toggle] Failed to update cart:', error);
    } finally {
      cartItems?.setBusy(false);
      // Guard against morphSection having already replaced this element
      if (this.isConnected) {
        this.refs.toggleSwitch.disabled = false;
      }
    }
  }
}

if (!customElements.get('subscription-toggle')) {
  customElements.define('subscription-toggle', SubscriptionToggle);
}
